import { useMemo } from 'react'

import { digObject, matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { DataStoreItemModel, DataStoreItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['dataStoreItems']

type DataStoreItemFilters = {
  hidden?: boolean,
  limit?: number,
  owner_id?: number,
  owner_type?: string,
  string?: string,
}

type UseDataStoreItemsOptions = {
  filters?: DataStoreItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: DataStoreItemRequestOptions,
}

function useDataStoreItems(options: UseDataStoreItemsOptions) {
  const { filters = {}, requestOptions, performHttpRequests } = options
  const {
    hidden: filterHidden,
    limit: filterLimit,
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
  } = filters

  const {
    updatedEntities: { dataStoreItems: dataStoreItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { dataStoreItems } = useSelector(reduxState => reduxState.entities)

  const filteredDataStoreItems = useMemo(() => {
    const filtered = Object.values(dataStoreItems).filter((dataStoreItem: DataStoreItemModel) => {
      const { data, owner_id, owner_type } = dataStoreItem

      // @ts-expect-error TS2345
      const hidden = digObject(data, 'hidden', false)
      const matchHidden = filterHidden ? hidden.toString() === filterHidden : true

      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType ? owner_type === filterOwnerType : true

      return matchHidden && matchOwnerId && matchOwnerType
    })

    return sortArrayBy(filtered, 'asc')
  }, [dataStoreItemsUpdatedAt, JSON.stringify(filters)])

  const filteredDataStoreItemsCount = filteredDataStoreItems.length
  const hasFilteredDataStoreItems = !!filteredDataStoreItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredDataStoreItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
  } = loadMorePayload

  const { creating, loading: loadingDataStoreItems } = useReduxAction(
    'dataStoreItems',
    'loadDataStoreItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2322
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    creating,
    canLoadMore,
    filteredDataStoreItems,
    filteredDataStoreItemsCount,
    hasFilteredDataStoreItems,
    loading: loadingDataStoreItems,
  }
}

export default useDataStoreItems
